/**
 * Length rules for meta title/description (TICKET-10023). Kept separate from
 * useSEO.ts so blog pages (which build title/description themselves before
 * calling useHead) can reuse the same tightening logic.
 */

const TITLE_MAX = 60;
const DESCRIPTION_MAX = 155;

function trimAtWordBoundary(text: string, maxLength: number): string {
    if (text.length <= maxLength) return text;
    const truncated = text.slice(0, maxLength);
    const lastSpace = truncated.lastIndexOf(' ');
    return (lastSpace > 0 ? truncated.slice(0, lastSpace) : truncated).trimEnd();
}

/**
 * Fits `raw` (already suffixed with e.g. " | Unified.to" if desired) within the
 * SERP-safe title length. The suffix is dropped before the title itself is
 * trimmed, per the "count includes suffix; drop suffix before keyword" rule.
 */
export function tightenSeoTitle(raw: string, suffix = ''): string {
    if (!raw) return raw;
    const withSuffix = suffix && !raw.includes('|') ? `${raw}${suffix}` : raw;
    if (withSuffix.length <= TITLE_MAX) return withSuffix;
    if (raw.length <= TITLE_MAX) return raw;
    return trimAtWordBoundary(raw, TITLE_MAX);
}

/** Caps a meta description at ~155 chars, trimmed at a word boundary. Never stretches a short one. */
export function trimSeoDescription(raw: string): string {
    if (!raw) return raw;
    if (raw.length <= DESCRIPTION_MAX) return raw;
    return trimAtWordBoundary(raw, 150);
}

// Unresolved CMS variables such as `{{numberofcategories}}` (TICKET-11097).
const CMS_VARIABLE_RE = /\{\{[^{}]*\}\}/g;

export function hasCmsVariable(text: string | undefined | null): boolean {
    return !!text && new RegExp(CMS_VARIABLE_RE.source).test(text);
}

/** Removes leftover `{{variable}}` tokens and tidies the whitespace they leave behind. */
export function stripCmsVariables(text: string): string {
    return text
        .replace(CMS_VARIABLE_RE, '')
        .replace(/\s{2,}/g, ' ')
        .replace(/\s+([,.;:!?])/g, '$1')
        .trim();
}

/**
 * First sentence of an article body as plain text, skipping headings, rules, dates and any line
 * that still contains an unresolved variable. The body already has its variables filled in, so this
 * is the substituted version of the "first sentence" description. Undefined if nothing usable.
 */
export function firstSentenceFromMarkdown(markdown: string | undefined): string | undefined {
    for (const rawLine of (markdown || '').split('\n')) {
        if (/^\s*#/.test(rawLine)) continue;
        let line = rawLine.replace(/!\[[^\]]*\]\([^)]*\)/g, '');
        // Links can be nested (`[Unified [MCP](/mcp)](/mcp)`), so unwrap until none are left.
        while (/\[([^\]]*)\]\([^)]*\)/.test(line)) line = line.replace(/\[([^\]]*)\]\([^)]*\)/g, '$1');
        line = line
            .replace(/<[^>]+>/g, '')
            .replace(/[*_`>#]/g, '')
            .trim();
        if (line.length < 20 || /^[-=|]+$/.test(line) || hasCmsVariable(line)) continue;
        const match = line.match(/^.*?[.!?](?=\s|$)/);
        return (match ? match[0] : line).trim();
    }
    return undefined;
}

/**
 * Meta text that is safe to publish: unchanged when it has no variable; otherwise the substituted
 * `fallback` if provided, otherwise the text with the variable tokens removed.
 */
export function resolveMetaText(text: string | undefined, fallback?: string): string | undefined {
    if (!text || !hasCmsVariable(text)) return text;
    if (fallback && !hasCmsVariable(fallback)) return fallback;
    return stripCmsVariables(text);
}
